import React, { useMemo, useState } from 'react';
import { BG, C, CARD_STYLE, St, FONT_BODY, SPRING } from '../theme';
import { classifyAllExercises4State, calcAvg1RM, calcVolume, detectStalls, calcSessionHardSets, filterWarmupSets, calcOverallProgressionTrend } from '../science';
import { NavArrowLeft } from 'iconoir-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { _globalSettings } from '../constants';

const MONTHS = ['gen','feb','mar','apr','mag','giu','lug','ago','set','ott','nov','dic'];
function fmtDate(iso) {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

const STATE_ORDER = ['PR','Progressione','Stallo','Regressione'];
const STATE_COLORS = {
  PR:           { bg: '#CCFF0022', border: '#CCFF0066', color: '#5A7A00' },
  Progressione: { bg: C.greenL,    border: C.greenB,    color: C.green   },
  Stallo:       { bg: C.amberL,    border: C.amberB,    color: C.amber   },
  Regressione:  { bg: C.redL,      border: C.redB,      color: C.red     },
};

function StatePill({ state }) {
  const s = STATE_COLORS[state] || { bg: C.fill, border: C.border, color: C.subtle };
  return (
    <span style={{
      padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      fontFamily: FONT_BODY, whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {state}
    </span>
  );
}

function SectionTitle({ children, mt }) {
  return (
    <div style={{
      fontSize: 13, fontWeight: 600, color: C.subtle, fontFamily: FONT_BODY,
      textTransform: 'uppercase', letterSpacing: 0.4,
      marginTop: mt !== undefined ? mt : 20, marginBottom: 8,
    }}>
      {children}
    </div>
  );
}

// ─── REVIEW MENSILE ───────────────────────────────────────────────────────────
const MONTH_NAMES_IT=['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const MONTH_COLORS=[C.blue,C.purple,C.green,C.amber,C.cyan,C.red,C.blue,C.purple,C.green,C.amber,C.cyan,C.red];

function IlluBarChart({color}){return(
  <svg width="80" height="64" viewBox="0 0 80 64" fill="none">
    <rect x="8" y="28" width="12" height="28" rx="4" fill={color} opacity="0.18"/>
    <rect x="24" y="16" width="12" height="40" rx="4" fill={color} opacity="0.40"/>
    <rect x="40" y="8" width="12" height="48" rx="4" fill={color} opacity="0.75"/>
    <rect x="56" y="20" width="12" height="36" rx="4" fill={color}/>
    <line x1="6" y1="58" x2="74" y2="58" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/>
  </svg>
);}
function IlluTrophy({color}){return(
  <svg width="80" height="64" viewBox="0 0 80 64" fill="none">
    <path d="M40 6 L50 24 L70 26 L55 40 L59 60 L40 50 L21 60 L25 40 L10 26 L30 24 Z" fill={color} opacity="0.15"/>
    <path d="M40 12 L47 28 L64 30 L52 42 L55 58 L40 50 L25 58 L28 42 L16 30 L33 28 Z" fill={color} opacity="0.5"/>
    <circle cx="40" cy="34" r="8" fill={color}/>
  </svg>
);}
function IlluLightning({color}){return(
  <svg width="80" height="64" viewBox="0 0 80 64" fill="none">
    <polygon points="46,4 20,36 38,36 34,60 60,28 42,28" fill={color} opacity="0.18"/>
    <polygon points="46,10 24,38 40,38 36,56 56,30 40,30" fill={color} opacity="0.55"/>
    <polygon points="46,16 30,40 42,40 38,52 54,32 42,32" fill={color}/>
  </svg>
);}
function IlluCalendar({color}){return(
  <svg width="80" height="64" viewBox="0 0 80 64" fill="none">
    <rect x="10" y="12" width="60" height="46" rx="8" fill={color} opacity="0.10"/>
    <rect x="10" y="12" width="60" height="16" rx="8" fill={color} opacity="0.30"/>
    <rect x="10" y="20" width="60" height="8" fill={color} opacity="0.20"/>
    {[0,1,2,3,4,5].map(i=><circle key={i} cx={22+i*12} cy="44" r="4" fill={color} opacity={i<3?0.8:0.3}/>)}
  </svg>
);}
function IlluArrow({color}){return(
  <svg width="80" height="64" viewBox="0 0 80 64" fill="none">
    <path d="M8 48 Q20 40 32 30 Q44 20 56 16" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.3"/>
    <path d="M8 52 Q20 44 32 34 Q44 24 56 20" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.6"/>
    <path d="M8 56 Q20 48 32 38 Q44 28 56 24" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none"/>
    <polygon points="56,16 72,24 56,32" fill={color}/>
  </svg>
);}
function IlluTarget({color}){return(
  <svg width="80" height="64" viewBox="0 0 80 64" fill="none">
    <circle cx="40" cy="32" r="26" stroke={color} strokeWidth="1.5" opacity="0.18"/>
    <circle cx="40" cy="32" r="18" stroke={color} strokeWidth="2" opacity="0.40"/>
    <circle cx="40" cy="32" r="10" stroke={color} strokeWidth="2" opacity="0.70"/>
    <circle cx="40" cy="32" r="4" fill={color}/>
    <line x1="66" y1="8" x2="46" y2="28" stroke={color} strokeWidth="2" strokeLinecap="round"/>
    <polygon points="68,4 70,14 60,10" fill={color}/>
  </svg>
);}
const MONTH_ILLUSTRATIONS=[IlluBarChart,IlluTrophy,IlluLightning,IlluCalendar,IlluArrow,IlluTarget,IlluBarChart,IlluTrophy,IlluLightning,IlluCalendar,IlluArrow,IlluTarget];

function hexToRgbStats(hex){
  const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
  return`${r},${g},${b}`;
}

function MonthReviewOverlay({monthData,allSessions,onClose}){
  const {key,sessions}=monthData;
  const [,mo]=key.split('-');
  const moIdx=parseInt(mo,10)-1;
  const moName=MONTH_NAMES_IT[moIdx];
  const accentCol=MONTH_COLORS[moIdx%MONTH_COLORS.length];
  const IllComp=MONTH_ILLUSTRATIONS[moIdx%MONTH_ILLUSTRATIONS.length];

  const sorted=useMemo(()=>[...sessions].sort((a,b)=>a.date.localeCompare(b.date)),[sessions]);
  const totalVol=useMemo(()=>sorted.reduce((acc,s)=>acc+s.exercises.reduce((a,e)=>a+calcVolume(e.sets||[]),0),0),[sorted]);
  const totalSets=useMemo(()=>sorted.reduce((acc,s)=>acc+calcSessionHardSets(s.exercises),0),[sorted]);

  const exStats=useMemo(()=>{
    const map={};
    sorted.forEach(s=>{
      s.exercises.forEach(ex=>{
        if(!map[ex.name])map[ex.name]={name:ex.name,totalVol:0,max1rm:0};
        map[ex.name].totalVol+=calcVolume(ex.sets||[]);
        const w=filterWarmupSets(ex.sets||[]);
        const rm=calcAvg1RM(w);
        if(rm>map[ex.name].max1rm)map[ex.name].max1rm=rm;
      });
    });
    return Object.values(map).sort((a,b)=>b.totalVol-a.totalVol);
  },[sorted]);

  const preSessions=useMemo(()=>allSessions.filter(s=>s.date<key+'-01'),[allSessions,key]);

  const progressioniCount=useMemo(()=>{
    let cnt=0;
    exStats.forEach(ex=>{
      const lastInMonth=sorted.filter(s=>s.exercises.some(e=>e.name===ex.name)).slice(-1)[0];
      if(!lastInMonth)return;
      const curEx=lastInMonth.exercises.find(e=>e.name===ex.name);
      const prevSess=preSessions.slice().reverse().find(s=>s.exercises.some(e=>e.name===ex.name));
      if(!prevSess)return;
      const prevEx=prevSess.exercises.find(e=>e.name===ex.name);
      if(calcVolume(curEx.sets||[])>calcVolume(prevEx.sets||[]))cnt++;
    });
    return cnt;
  },[exStats,sorted,preSessions]);

  const prList=useMemo(()=>exStats.filter(ex=>{
    if(ex.max1rm<=0)return false;
    const bestPre=preSessions.reduce((best,s)=>{
      const e=s.exercises.find(e=>e.name===ex.name);
      if(!e)return best;
      return Math.max(best,calcAvg1RM(filterWarmupSets(e.sets||[])));
    },0);
    return ex.max1rm>bestPre;
  }),[exStats,preSessions]);

  const stallsRaw=useMemo(()=>detectStalls(allSessions),[allSessions]);
  const monthStalls=useMemo(()=>stallsRaw.filter(st=>st.lastDate&&st.lastDate.slice(0,7)===key).map(st=>st.name),[stallsRaw,key]);

  const bestSession=useMemo(()=>{
    if(!sorted.length)return null;
    return sorted.reduce((best,s)=>{
      const v=s.exercises.reduce((a,e)=>a+calcVolume(e.sets||[]),0);
      return v>best.exercises.reduce((a,e)=>a+calcVolume(e.sets||[]),0)?s:best;
    });
  },[sorted]);

  const weekBars=useMemo(()=>{
    const wMap={};
    sorted.forEach(s=>{
      const d=new Date(s.date);
      const mon=new Date(d);mon.setDate(d.getDate()-((d.getDay()+6)%7));
      const wk=`${mon.getFullYear()}-${String(mon.getMonth()+1).padStart(2,'0')}-${String(mon.getDate()).padStart(2,'0')}`;
      wMap[wk]=(wMap[wk]||0)+s.exercises.reduce((a,e)=>a+calcVolume(e.sets||[]),0);
    });
    return Object.keys(wMap).sort().map((k,i)=>({label:`S${i+1}`,vol:Math.round(wMap[k])}));
  },[sorted]);
  const maxBar=weekBars.length?Math.max(...weekBars.map(b=>b.vol),1):1;

  const SPRING_OVERLAY='cubic-bezier(0.32,0.72,0,1)';
  return(
    <div style={{position:'fixed',inset:0,zIndex:400,background:BG,display:'flex',flexDirection:'column',overflowY:'auto',animation:`gpFadeUpRev 0.35s ${SPRING_OVERLAY} both`}}>
      <style>{`@keyframes gpFadeUpRev{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{position:'sticky',top:0,zIndex:10,background:`rgba(${hexToRgbStats(BG)},0.94)`,backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',paddingTop:'env(safe-area-inset-top,12px)'}}>
        <div style={{padding:'10px 16px 12px',borderBottom:`0.5px solid ${C.border}`}}>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:5,color:accentCol,padding:0,fontFamily:FONT_BODY,fontSize:15,fontWeight:500,marginBottom:10}}>
            <NavArrowLeft width={18} height={18} color={accentCol}/>
            Indietro
          </button>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:48,height:48,borderRadius:14,background:`${accentCol}15`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <IllComp color={accentCol}/>
            </div>
            <div>
              <div style={{fontSize:22,fontWeight:800,color:C.text,letterSpacing:-0.7,lineHeight:1.1}}>Review {moName}</div>
              <div style={{fontSize:12,color:C.subtle,marginTop:3}}>{sessions.length} sessioni</div>
            </div>
          </div>
        </div>
      </div>
      <div style={{padding:'16px 16px max(40px,env(safe-area-inset-bottom,20px)) 16px',display:'flex',flexDirection:'column',gap:12}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
          {[
            {label:'Volume',value:totalVol>=1000?`${(totalVol/1000).toFixed(1)}k`:Math.round(totalVol).toLocaleString(),col:accentCol},
            {label:'Hard set',value:totalSets,col:C.purple},
            {label:'PR',value:prList.length,col:C.green},
          ].map(kpi=>(
            <div key={kpi.label} style={{background:'#fff',borderRadius:14,padding:'14px 12px',boxShadow:'0 1px 8px rgba(0,0,0,0.05)'}}>
              <div style={{fontSize:22,fontWeight:800,color:kpi.col,letterSpacing:-1,lineHeight:1}}>{kpi.value}</div>
              <div style={{fontSize:10,color:C.subtle,marginTop:3,fontWeight:500,textTransform:'uppercase',letterSpacing:0.3}}>{kpi.label}</div>
            </div>
          ))}
        </div>
        {weekBars.length>0&&(
          <div style={{background:'#fff',borderRadius:16,padding:'16px 16px 12px',boxShadow:'0 1px 8px rgba(0,0,0,0.05)'}}>
            <div style={{fontSize:13,fontWeight:600,color:C.muted,textTransform:'uppercase',letterSpacing:0.4,marginBottom:12}}>Volume per settimana</div>
            <div style={{display:'flex',alignItems:'flex-end',gap:6,height:60}}>
              {weekBars.map((b,i)=>(
                <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                  <div style={{width:'100%',borderRadius:5,background:accentCol,opacity:0.2+0.8*(b.vol/maxBar),height:Math.max(6,Math.round(52*(b.vol/maxBar)))}}/>
                  <div style={{fontSize:10,color:C.subtle,fontWeight:500}}>{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          <div style={{background:'#fff',borderRadius:14,padding:'14px 14px',boxShadow:'0 1px 8px rgba(0,0,0,0.05)'}}>
            <div style={{fontSize:22,fontWeight:800,color:C.green,letterSpacing:-1,lineHeight:1}}>{progressioniCount}</div>
            <div style={{fontSize:10,color:C.subtle,marginTop:3,fontWeight:500,textTransform:'uppercase',letterSpacing:0.3}}>Progressioni</div>
          </div>
          <div style={{background:'#fff',borderRadius:14,padding:'14px 14px',boxShadow:'0 1px 8px rgba(0,0,0,0.05)'}}>
            <div style={{fontSize:22,fontWeight:800,color:monthStalls.length>0?C.amber:C.subtle,letterSpacing:-1,lineHeight:1}}>{monthStalls.length}</div>
            <div style={{fontSize:10,color:C.subtle,marginTop:3,fontWeight:500,textTransform:'uppercase',letterSpacing:0.3}}>Stalli</div>
          </div>
        </div>
        {bestSession&&(
          <div style={{background:'#fff',borderRadius:16,padding:'16px',boxShadow:'0 1px 8px rgba(0,0,0,0.05)'}}>
            <div style={{fontSize:13,fontWeight:600,color:C.muted,textTransform:'uppercase',letterSpacing:0.4,marginBottom:10}}>Sessione migliore</div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:8,height:8,borderRadius:4,background:accentCol,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:700,color:C.text}}>{bestSession.workoutName||'Allenamento'}</div>
                <div style={{fontSize:12,color:C.subtle,marginTop:2}}>{new Date(bestSession.date).toLocaleDateString('it-IT',{weekday:'short',day:'numeric',month:'long'})}</div>
              </div>
              <div style={{fontSize:13,fontWeight:700,color:accentCol}}>{Math.round(bestSession.exercises.reduce((a,e)=>a+calcVolume(e.sets||[]),0)).toLocaleString()} kg</div>
            </div>
          </div>
        )}
        {prList.length>0&&(
          <div style={{background:'#fff',borderRadius:16,padding:'16px',boxShadow:'0 1px 8px rgba(0,0,0,0.05)'}}>
            <div style={{fontSize:13,fontWeight:600,color:C.muted,textTransform:'uppercase',letterSpacing:0.4,marginBottom:10}}>PR del mese</div>
            {prList.slice(0,5).map((ex,i,arr)=>(
              <div key={ex.name} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 0',borderBottom:i<Math.min(arr.length,5)-1?`0.5px solid ${C.border}`:'none'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:24,height:24,borderRadius:8,background:`${C.green}15`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                  </div>
                  <div style={{fontSize:14,fontWeight:600,color:C.text,maxWidth:160,overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>{ex.name}</div>
                </div>
                {ex.max1rm>0&&<span style={{fontSize:13,fontWeight:700,color:C.green}}>{ex.max1rm.toFixed(0)} kg 1RM</span>}
              </div>
            ))}
          </div>
        )}
        {monthStalls.length>0&&(
          <div style={{background:'#fff',borderRadius:16,padding:'16px',boxShadow:'0 1px 8px rgba(0,0,0,0.05)'}}>
            <div style={{fontSize:13,fontWeight:600,color:C.muted,textTransform:'uppercase',letterSpacing:0.4,marginBottom:10}}>Da sbloccare</div>
            {monthStalls.slice(0,4).map((name,i,arr)=>(
              <div key={name} style={{display:'flex',alignItems:'center',gap:8,padding:'10px 0',borderBottom:i<Math.min(arr.length,4)-1?`0.5px solid ${C.border}`:'none'}}>
                <div style={{width:24,height:24,borderRadius:8,background:`${C.amber}15`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.amber} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4m0 4h.01"/><circle cx="12" cy="12" r="9"/></svg>
                </div>
                <div style={{fontSize:14,fontWeight:600,color:C.text}}>{name}</div>
              </div>
            ))}
          </div>
        )}
        {exStats.length>0&&(
          <div style={{background:'#fff',borderRadius:16,padding:'16px',boxShadow:'0 1px 8px rgba(0,0,0,0.05)'}}>
            <div style={{fontSize:13,fontWeight:600,color:C.muted,textTransform:'uppercase',letterSpacing:0.4,marginBottom:10}}>Top esercizi</div>
            {exStats.slice(0,5).map((ex,i,arr)=>{
              const pct=totalVol>0?ex.totalVol/totalVol:0;
              return(
                <div key={ex.name} style={{padding:'8px 0',borderBottom:i<Math.min(arr.length,5)-1?`0.5px solid ${C.border}`:'none'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:5}}>
                    <div style={{fontSize:14,fontWeight:600,color:C.text,maxWidth:200,overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>{ex.name}</div>
                    <div style={{fontSize:12,fontWeight:700,color:C.muted}}>{Math.round(ex.totalVol).toLocaleString()} kg</div>
                  </div>
                  <div style={{height:4,borderRadius:2,background:C.fill,overflow:'hidden'}}>
                    <div style={{height:'100%',borderRadius:2,background:accentCol,width:`${Math.round(pct*100)}%`,opacity:0.7}}/>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{background:'#fff',borderRadius:16,padding:'16px',boxShadow:'0 1px 8px rgba(0,0,0,0.05)'}}>
          <div style={{fontSize:13,fontWeight:600,color:C.muted,textTransform:'uppercase',letterSpacing:0.4,marginBottom:10}}>Sessioni del mese</div>
          {sorted.map((s,i)=>{
            const v=Math.round(s.exercises.reduce((a,e)=>a+calcVolume(e.sets||[]),0));
            const dLabel=new Date(s.date).toLocaleDateString('it-IT',{weekday:'short',day:'numeric',month:'short'});
            return(
              <div key={s.id||i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 0',borderBottom:i<sorted.length-1?`0.5px solid ${C.border}`:'none'}}>
                <div style={{width:8,height:8,borderRadius:4,background:accentCol,flexShrink:0}}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:600,color:C.text}}>{s.workoutName||'Allenamento'}</div>
                  <div style={{fontSize:11,color:C.subtle,marginTop:1,textTransform:'capitalize'}}>{dLabel}</div>
                </div>
                {v>0&&<div style={{fontSize:13,fontWeight:700,color:C.muted}}>{v.toLocaleString()} kg</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function StatsPage({ db }) {
  const { sessions = [], weightLog = [] } = db;
  const [progExpanded, setProgExpanded] = useState(false);
  const [volView, setVolView] = useState('1Y');
  const [selectedMonthReview, setSelectedMonthReview] = useState(null);

  const monthsWithSessions = useMemo(() => {
    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
    const map = {};
    sessions.forEach(s => {
      if (!s.date) return;
      const k = s.date.slice(0, 7);
      if (k >= currentKey) return;
      if (!map[k]) map[k] = [];
      map[k].push(s);
    });
    return Object.keys(map).sort((a, b) => b.localeCompare(a)).map(k => ({ key: k, sessions: map[k] }));
  }, [sessions]);

  const perfMap = useMemo(() => classifyAllExercises4State(sessions), [sessions]);

  const grouped = useMemo(() => {
    const g = { PR: [], Progressione: [], Stallo: [], Regressione: [] };
    Object.entries(perfMap).forEach(([name, info]) => {
      if (g[info.state]) g[info.state].push({ name, ...info });
    });
    return g;
  }, [perfMap]);

  const totalEx = Object.values(grouped).reduce((s, a) => s + a.length, 0);

  const top1RM = useMemo(() => {
    const byEx = {};
    sessions.forEach(sess => {
      sess.exercises.forEach(ex => {
        const rm = calcAvg1RM(ex.sets);
        if (rm > 0) {
          if (!byEx[ex.name]) byEx[ex.name] = [];
          byEx[ex.name].push({ date: sess.date, rm });
        }
      });
    });
    return Object.entries(byEx)
      .map(([name, entries]) => {
        const sorted = [...entries].sort((a, b) => b.date.localeCompare(a.date));
        const latest = sorted[0]?.rm || 0;
        const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 28);
        const old = sorted.find(e => new Date(e.date) < cutoff);
        const delta = old ? Math.round((latest - old.rm) * 10) / 10 : null;
        return { name, rm: Math.round(latest), delta };
      })
      .filter(e => e.rm > 0)
      .sort((a, b) => b.rm - a.rm)
      .slice(0, 5);
  }, [sessions]);

  const weightData30 = useMemo(() => {
    if (!weightLog.length) return [];
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    return [...weightLog]
      .filter(w => new Date(w.date) >= cutoff)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(w => ({ date: fmtDate(w.date), peso: w.weight }));
  }, [weightLog]);

  // ── Volume line chart (stessa logica di Dashboard) ──────────────────────
  const PERIODS = [
    { v: '1M', label: '1M', days: 30 },
    { v: '3M', label: '3M', days: 90 },
    { v: '6M', label: '6M', days: 180 },
    { v: '1Y', label: '1A', days: 365 },
  ];
  const volPeriod = PERIODS.find(p => p.v === volView) || PERIODS[3];
  const volWeekPoints = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now); cutoff.setDate(now.getDate() - volPeriod.days);
    const cutoffISO = cutoff.toISOString().slice(0, 10);
    const filtered = sessions.filter(s => s.date >= cutoffISO);
    const totalVol = Math.round(filtered.reduce((acc, s) => acc + calcVolume(s.exercises.flatMap(e => e.sets || [])), 0));
    const weekMap = {};
    filtered.forEach(s => {
      const d = new Date(s.date);
      const dow = (d.getDay() + 6) % 7;
      const mon = new Date(d); mon.setDate(d.getDate() - dow);
      const key = mon.toISOString().slice(0, 10);
      weekMap[key] = (weekMap[key] || 0) + calcVolume(s.exercises.flatMap(e => e.sets || []));
    });
    const points = Object.entries(weekMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => ({ date: k, vol: Math.round(v) }));
    return { points, totalVol };
  }, [sessions, volPeriod.days]);

  const { points: weekPoints, totalVol } = volWeekPoints;
  const hasVolData = weekPoints.length >= 2;
  const W = 320, H = 72, PAD_T = 8, PAD_B = 8;
  const iH = H - PAD_T - PAD_B;
  const maxV = hasVolData ? Math.max(...weekPoints.map(p => p.vol), 1) : 1;
  const n = weekPoints.length;
  const px = i => i / (n > 1 ? n - 1 : 1) * W;
  const py = v => PAD_T + iH - (v / maxV) * iH;
  const pts = weekPoints.map((p, i) => ({ x: px(i), y: py(p.vol), vol: p.vol }));
  const polyline = pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const fillPath = pts.length >= 2
    ? `M ${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)} ` + pts.slice(1).map(p => `L ${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') + ` L ${pts[n-1].x.toFixed(1)},${H} L ${pts[0].x.toFixed(1)},${H} Z`
    : '';
  const lastPt = pts[pts.length - 1];

  // ── Riepilogo settimana ───────────────────────────────────────────────────
  const thisWeekKey = (() => {
    const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d.toISOString().split('T')[0];
  })();
  const prevWeekKey = (() => {
    const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7) - 7);
    return d.toISOString().split('T')[0];
  })();
  const sessionsThisWeek = sessions.filter(s => s.date >= thisWeekKey).length;
  const thisWeekVolRaw = Math.round(
    sessions.filter(s => s.date >= thisWeekKey)
      .reduce((acc, s) => acc + calcVolume(s.exercises.flatMap(e => e.sets || [])), 0)
  );
  const prevWeekVolRaw = Math.round(
    sessions.filter(s => s.date >= prevWeekKey && s.date < thisWeekKey)
      .reduce((acc, s) => acc + calcVolume(s.exercises.flatMap(e => e.sets || [])), 0)
  );
  const volDelta = prevWeekVolRaw > 0 ? Math.round(((thisWeekVolRaw - prevWeekVolRaw) / prevWeekVolRaw) * 100) : null;

  // ── Obiettivo (da localStorage, read-only) ───────────────────────────────
  const goalEx = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('rc_goal_widget')) || null; } catch { return null; }
  }, []);
  const currentValue = useMemo(() => {
    if (!goalEx) return null;
    if (goalEx.metric === 'peso') {
      const sorted = [...weightLog].sort((a, b) => b.date.localeCompare(a.date));
      return sorted[0]?.weight ?? null;
    }
    const byEx = {};
    sessions.forEach(sess => {
      sess.exercises.forEach(ex => {
        if (ex.name.toLowerCase() === goalEx.exerciseName?.toLowerCase()) {
          const rm = calcAvg1RM(ex.sets);
          if (rm > 0) byEx[sess.date] = rm;
        }
      });
    });
    const sorted = Object.entries(byEx).sort(([a], [b]) => b.localeCompare(a));
    return sorted[0] ? Math.round(sorted[0][1]) : null;
  }, [goalEx, sessions, weightLog]);

  // ── Peso ─────────────────────────────────────────────────────────────────
  const lastEntry = [...weightLog].sort((a, b) => b.date.localeCompare(a.date))[0] || null;
  const lastW = lastEntry?.weight ?? (parseFloat(_globalSettings.profilo?.peso) || null);
  const sortedLog = [...weightLog].sort((a, b) => a.date.localeCompare(b.date));
  const prevEntry = sortedLog.length >= 2 ? sortedLog[sortedLog.length - 2] : null;
  const entryDelta = lastEntry && prevEntry ? lastEntry.weight - prevEntry.weight : null;

  // Sparkline per widget peso compatto
  const sparkEntries = [...weightLog].sort((a, b) => a.date.localeCompare(b.date)).slice(-8);
  const sparklineEl = (() => {
    if (sparkEntries.length < 2) return null;
    const sparkW = sparkEntries.map(e => e.weight);
    const minSW = Math.min(...sparkW); const maxSW = Math.max(...sparkW);
    const rangeSW = maxSW - minSW || 1;
    const SW = 200, SH = 40, PAD = 4;
    const plotW = SW - PAD * 2; const plotH = SH - PAD * 2;
    const spx = i => PAD + i * (plotW / (sparkEntries.length - 1));
    const spy = v => PAD + plotH - (((v - minSW) / rangeSW) * plotH);
    const spts = sparkEntries.map((e, i) => ({ x: spx(i), y: spy(e.weight) }));
    const sPolyline = spts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    return (
      <svg viewBox={`0 0 ${SW} ${SH}`} style={{ width: '100%', height: 40, display: 'block', overflow: 'visible', marginTop: 8 }}>
        <polyline points={sPolyline} fill="none" stroke={C.blue} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={spts[spts.length - 1].x} cy={spts[spts.length - 1].y} r={3} fill={C.blue} />
      </svg>
    );
  })();

  // ── Metriche corporee ─────────────────────────────────────────────────────
  const bodyMetrics = useMemo(() => {
    const p = _globalSettings.profilo || {};
    const w = lastW || parseFloat(p.peso) || null;
    const h = parseFloat(p.altezza) || null;
    if (!w || !h) return null;
    const bmi = Math.round((w / Math.pow(h / 100, 2)) * 10) / 10;
    const bmiCat = bmi < 18.5 ? 'Sottopeso' : bmi < 25 ? 'Normopeso' : bmi < 30 ? 'Sovrappeso' : 'Obesità';
    const bmiCol = bmi < 18.5 ? C.amber : bmi < 25 ? C.green : bmi < 30 ? C.amber : C.red;
    let age = null;
    if (p.dataNascita) {
      const dob = new Date(p.dataNascita); const today = new Date();
      age = today.getFullYear() - dob.getFullYear() - (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
    } else if (p.eta) age = parseFloat(p.eta);
    const bmr = age ? Math.round(p.sesso === 'F' ? 10 * w + 6.25 * h - 5 * age - 161 : 10 * w + 6.25 * h - 5 * age + 5) : null;
    const tdee = bmr ? Math.round(bmr * (parseFloat(p.livelloAttivita) || 1.55)) : null;
    return { bmi, bmiCat, bmiCol, bmr, tdee };
  }, [lastW]);

  const PROGRESS_LIMIT = 5;
  const statesToShow = STATE_ORDER.filter(s => grouped[s]?.length > 0);

  // ── Progressione globale ──────────────────────────────────────────────────
  const _PT_MONTHS=['gen','feb','mar','apr','mag','giu','lug','ago','set','ott','nov','dic'];
  const progTrend=useMemo(()=>calcOverallProgressionTrend(sessions),[sessions]);
  const hasTrend=progTrend.length>=3;
  const lastSlope=hasTrend?progTrend[progTrend.length-1].avgSlope:null;
  const trendColor=lastSlope===null?C.subtle:lastSlope>0.05?C.green:lastSlope<-0.05?C.red:C.amber;
  const PT_W=320,PT_H=240,PT_PAD_T=12,PT_PAD_B=28,PT_PAD_L=2,PT_PAD_R=34;
  const ptPlotW=PT_W-PT_PAD_L-PT_PAD_R,ptPlotH=PT_H-PT_PAD_T-PT_PAD_B;
  const ptSlopes=hasTrend?progTrend.map(p=>p.avgSlope):[];
  const ptMin=hasTrend?Math.min(...ptSlopes,0):0;
  const ptMax=hasTrend?Math.max(...ptSlopes,0.01):0.01;
  const ptRange=ptMax-ptMin||0.01;
  const ptN=progTrend.length;
  const ptPx=i=>PT_PAD_L+(i/(ptN>1?ptN-1:1))*ptPlotW;
  const ptPy=v=>PT_PAD_T+ptPlotH-((v-ptMin)/ptRange)*ptPlotH;
  const ptZeroY=ptPy(0);
  const ptPeakSlope=hasTrend?Math.max(...ptSlopes):null;
  const ptPeakY=hasTrend?ptPy(ptPeakSlope):null;
  const ptPts=hasTrend?progTrend.map((p,i)=>({x:ptPx(i),y:ptPy(p.avgSlope),v:p.avgSlope,week:p.week})):[];
  const ptPolyline=ptPts.map(p=>`${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const ptLastPt=ptPts[ptPts.length-1];
  const ptFillPath=ptPts.length>=2
    ?`M ${ptPts[0].x.toFixed(1)},${ptPts[0].y.toFixed(1)} `+ptPts.slice(1).map(p=>`L ${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')+` L ${ptPts[ptN-1].x.toFixed(1)},${ptZeroY.toFixed(1)} L ${ptPts[0].x.toFixed(1)},${ptZeroY.toFixed(1)} Z`:'';
  const ptMonthTicks=hasTrend?ptPts.reduce((acc,pt,i)=>{const m=pt.week.slice(0,7);if(!acc.seen.has(m)){acc.seen.add(m);acc.ticks.push({i,label:_PT_MONTHS[parseInt(m.slice(5,7),10)-1]});}return acc;},{seen:new Set(),ticks:[]}).ticks:[];
  const ptYLabels=hasTrend?[{v:ptMin,y:ptPy(ptMin)},{v:(ptMin+ptMax)/2,y:ptPy((ptMin+ptMax)/2)},{v:ptMax,y:ptPy(ptMax)}]:[];
  const ptLineLen=ptPts.length>1?Math.round(ptPlotW*1.3+ptPlotH*0.5):1000;

  return (
    <>
    <div style={{ ...St.page, background: BG }}>
      <div style={{ ...St.wrap, padding: '0 16px 40px' }}>
        <style>{`@keyframes gpSlideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}@keyframes gpFadeIn{from{opacity:0}to{opacity:1}}`}</style>

        {/* Header */}
        <div style={{ paddingTop: 'max(56px, calc(env(safe-area-inset-top, 0px) + 14px))', paddingBottom: 24, animation: `gpSlideUp 0.5s 0.0s ${SPRING} both` }}>
          <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: -1.2, lineHeight: 1, fontFamily: FONT_BODY }}>
            Statistiche
          </div>
          <div style={{ fontSize: 14, color: C.subtle, marginTop: 6, fontFamily: FONT_BODY, fontWeight: 400, letterSpacing: 0 }}>
            Progressi e analisi del tuo allenamento
          </div>
        </div>


        {/* ── Questa settimana ── */}
        <SectionTitle mt={0}>QUESTA SETTIMANA</SectionTitle>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10, animation: `gpSlideUp 0.5s 0.1s ${SPRING} both` }}>
          <div style={{ ...CARD_STYLE, flex: 1, padding: '16px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.subtle, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: FONT_BODY, marginBottom: 6 }}>Volume</div>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -1, color: C.text, fontFamily: FONT_BODY, lineHeight: 1 }}>{thisWeekVolRaw.toLocaleString('it-IT')}</div>
            <div style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY, marginTop: 2 }}>kg totali</div>
            {volDelta !== null && thisWeekVolRaw > 0 && (
              <div style={{ fontSize: 12, fontWeight: 600, color: volDelta >= 0 ? C.green : C.red, fontFamily: FONT_BODY, marginTop: 6 }}>
                {volDelta >= 0 ? '+' : ''}{volDelta}% vs settimana scorsa
              </div>
            )}
          </div>
          <div style={{ ...CARD_STYLE, flex: 1, padding: '16px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.subtle, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: FONT_BODY, marginBottom: 6 }}>Sessioni</div>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -1, color: C.text, fontFamily: FONT_BODY, lineHeight: 1 }}>{sessionsThisWeek}</div>
            <div style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY, marginTop: 2 }}>questa settimana</div>
            <div style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY, marginTop: 6 }}>{sessions.length} totali</div>
          </div>
        </div>

        {/* ── Volume line chart ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 8, animation: `gpFadeIn 0.4s 0.18s both` }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, fontFamily: FONT_BODY, textTransform: 'uppercase' }}>Volume</div>
          <div style={{ display: 'flex', background: C.fill, borderRadius: 9, padding: 2 }}>
            {PERIODS.map(p => (
              <button key={p.v} onClick={() => setVolView(p.v)}
                style={{ padding: '4px 10px', borderRadius: 7, border: 'none', fontFamily: FONT_BODY, fontSize: 12, fontWeight: volView === p.v ? 600 : 400, cursor: 'pointer', background: volView === p.v ? C.card : 'transparent', color: volView === p.v ? C.text : C.subtle, boxShadow: volView === p.v ? '0 1px 3px rgba(0,0,0,0.12)' : 'none', transition: 'all 0.15s' }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ ...St.card, animation: `gpSlideUp 0.5s 0.2s ${SPRING} both` }}>
          {hasVolData ? (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 11, color: C.subtle, fontWeight: 500, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.3 }}>Totale {volPeriod.v}</div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
                    <span style={{ fontSize: 32, fontWeight: 800, color: C.text, fontFamily: FONT_BODY, letterSpacing: -1, lineHeight: 1 }}>{totalVol.toLocaleString('it-IT')}</span>
                    <span style={{ fontSize: 13, color: C.subtle, fontWeight: 500, paddingBottom: 3 }}>kg</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: C.subtle, marginBottom: 1 }}>ultima sett.</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.blue, fontFamily: FONT_BODY, letterSpacing: -0.5 }}>{(weekPoints[weekPoints.length - 1]?.vol || 0).toLocaleString('it-IT')}</div>
                </div>
              </div>
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block', overflow: 'visible' }}>
                {fillPath && <path d={fillPath} fill={`${C.blue}12`} />}
                {pts.length >= 2 && <polyline points={polyline} fill="none" stroke={C.blue} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
                {lastPt && <><circle cx={lastPt.x} cy={lastPt.y} r={4} fill={C.blue} /><circle cx={lastPt.x} cy={lastPt.y} r={7} fill={`${C.blue}30`} /></>}
              </svg>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0', color: C.subtle, fontSize: 14, fontFamily: FONT_BODY }}>
              Nessun volume nel periodo selezionato
            </div>
          )}
        </div>

        {/* ── Progressione globale ── */}
        {hasTrend && (
          <>
            <SectionTitle>PROGRESSIONE GLOBALE</SectionTitle>
            <div style={{ ...CARD_STYLE, animation: `gpSlideUp 0.5s 0.22s ${SPRING} both` }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5 }}>
                  <span style={{ fontSize: 38, fontWeight: 800, color: trendColor, fontFamily: FONT_BODY, letterSpacing: -1.5, lineHeight: 1 }}>
                    {lastSlope > 0 ? '+' : ''}{lastSlope.toFixed(2)}
                  </span>
                  <span style={{ fontSize: 14, color: C.subtle, fontWeight: 500, paddingBottom: 5 }}>kg/sess</span>
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 12px', borderRadius: 20, background: `${trendColor}15`, border: `1px solid ${trendColor}35` }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: trendColor, fontFamily: FONT_BODY }}>
                    {lastSlope > 0.05 ? 'In crescita' : lastSlope < -0.05 ? 'In calo' : 'Stabile'}
                  </span>
                </div>
              </div>
              <svg viewBox={`0 0 ${PT_W} ${PT_H}`} style={{ width: '100%', display: 'block', overflow: 'hidden' }}>
                {ptMonthTicks.map(tick => (
                  <line key={tick.i} x1={ptPx(tick.i).toFixed(1)} y1={PT_PAD_T} x2={ptPx(tick.i).toFixed(1)} y2={PT_PAD_T + ptPlotH} stroke="rgba(120,120,128,0.10)" strokeWidth={1} />
                ))}
                {ptPeakY !== null && (
                  <line x1={PT_PAD_L} y1={ptPeakY.toFixed(1)} x2={PT_PAD_L + ptPlotW} y2={ptPeakY.toFixed(1)} stroke="rgba(120,120,128,0.18)" strokeWidth={1} strokeDasharray="4 3" />
                )}
                {ptZeroY > PT_PAD_T && ptZeroY < PT_PAD_T + ptPlotH && (
                  <line x1={PT_PAD_L} y1={ptZeroY.toFixed(1)} x2={PT_PAD_L + ptPlotW} y2={ptZeroY.toFixed(1)} stroke="rgba(120,120,128,0.22)" strokeWidth={1} />
                )}
                {ptFillPath && <path d={ptFillPath} fill={`${trendColor}0f`} style={{ animation: 'ptFill 1.2s 1.6s cubic-bezier(0.25,0.46,0.45,0.94) both' }} />}
                {ptPts.length >= 2 && (
                  <polyline points={ptPolyline} fill="none" stroke={trendColor} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round"
                    style={{ '--pt-len': ptLineLen, strokeDasharray: ptLineLen, strokeDashoffset: ptLineLen, animation: `ptDraw 1.8s 0.3s cubic-bezier(0.25,0.46,0.45,0.94) forwards` }} />
                )}
                {ptLastPt && (
                  <>
                    <circle cx={ptLastPt.x} cy={ptLastPt.y} r={5} fill="white" stroke={trendColor} strokeWidth={2.5}
                      style={{ transformOrigin: `${ptLastPt.x}px ${ptLastPt.y}px`, animation: 'ptDot 0.5s 2.0s cubic-bezier(0.34,1.56,0.64,1) both' }} />
                    <line x1={ptLastPt.x.toFixed(1)} y1={ptLastPt.y.toFixed(1)} x2={ptLastPt.x.toFixed(1)} y2={(PT_PAD_T + ptPlotH).toFixed(1)} stroke={trendColor} strokeWidth={1} strokeDasharray="3 2" opacity={0.4}
                      style={{ animation: 'ptFill 0.5s 2.1s ease both' }} />
                  </>
                )}
                {ptYLabels.map((lbl, i) => (
                  <text key={i} x={PT_W - 2} y={(lbl.y + 4).toFixed(1)} fontSize={9} textAnchor="end" fill="rgba(142,142,147,0.75)" fontFamily="inherit">
                    {lbl.v > 0 ? '+' : ''}{lbl.v.toFixed(1)}
                  </text>
                ))}
                {ptMonthTicks.map((tick, ti) => {
                  const isFirst = ti === 0;
                  const isLast = ti === ptMonthTicks.length - 1;
                  const anchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
                  return (
                    <text key={tick.i} x={ptPx(tick.i).toFixed(1)} y={(PT_H - 7).toFixed(1)} fontSize={9} textAnchor={anchor} fill="rgba(142,142,147,0.9)" fontFamily="inherit">
                      {tick.label}
                    </text>
                  );
                })}
              </svg>
              <div style={{ marginTop: 6, fontSize: 11, color: C.subtle, fontFamily: FONT_BODY }}>
                {`${progTrend.length} settimane · esercizi con almeno 5 sessioni`}
              </div>
            </div>
          </>
        )}

        {/* ── Obiettivo + Peso compatto ── */}
        {(goalEx || lastW) && (
          <>
            <SectionTitle>OBIETTIVO E PESO</SectionTitle>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>

              {/* Obiettivo */}
              {goalEx && (
                <div style={{ ...CARD_STYLE, padding: '14px 14px', flex: 1, minWidth: 0, marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.subtle, fontFamily: FONT_BODY, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Obiettivo</div>
                  {(() => {
                    const pct = goalEx.targetWeight > 0 ? Math.min(100, Math.round(((currentValue || 0) / goalEx.targetWeight) * 100)) : 0;
                    const col = pct >= 100 ? C.green : pct >= 75 ? C.blue : pct >= 50 ? C.amber : C.red;
                    const metricLabel = goalEx.metric === 'peso' ? 'kg peso' : 'kg 1RM';
                    return (
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontSize: 11, color: C.subtle, fontWeight: 500, marginBottom: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{goalEx.exerciseName}</div>
                          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, marginBottom: 6 }}>
                            <span style={{ fontSize: 28, fontWeight: 800, color: col, fontFamily: FONT_BODY, letterSpacing: -0.8, lineHeight: 1 }}>{currentValue || '0'}</span>
                            <span style={{ fontSize: 11, color: C.subtle, fontWeight: 500, paddingBottom: 2 }}>{metricLabel}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <span style={{ fontSize: 11, color: C.subtle }}>di {goalEx.targetWeight} kg</span>
                            <span style={{ fontSize: 13, fontWeight: 800, color: col, fontFamily: FONT_BODY }}>{pct}%</span>
                          </div>
                        </div>
                        <div>
                          <div style={{ height: 5, borderRadius: 3, background: C.fill, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: col, transition: 'width 0.6s ease' }} />
                          </div>
                          {pct >= 100 && <div style={{ fontSize: 11, fontWeight: 700, color: C.green, marginTop: 6 }}>Raggiunto!</div>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Peso compatto */}
              {lastW && (
                <div style={{ ...CARD_STYLE, padding: '14px 14px', flex: 1, minWidth: 0, marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.subtle, fontFamily: FONT_BODY, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Peso</div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, marginBottom: 2 }}>
                        <span style={{ fontSize: 28, fontWeight: 800, color: C.text, fontFamily: FONT_BODY, letterSpacing: -0.8, lineHeight: 1 }}>{lastW.toFixed(1)}</span>
                        <span style={{ fontSize: 11, color: C.subtle, fontWeight: 500, paddingBottom: 2 }}>kg</span>
                      </div>
                      {entryDelta !== null && entryDelta !== 0 && (
                        <div style={{ fontSize: 12, fontWeight: 600, color: entryDelta < 0 ? C.green : C.red, marginBottom: 2 }}>
                          {entryDelta > 0 ? '+' : ''}{entryDelta.toFixed(1)} kg
                        </div>
                      )}
                    </div>
                    {sparklineEl}
                  </div>
                </div>
              )}

            </div>
          </>
        )}

        {/* ── Andamento peso (30gg) line chart ── */}
        {weightData30.length > 1 && (
          <>
            <SectionTitle>ANDAMENTO PESO (30 GG)</SectionTitle>
            <div style={{ ...CARD_STYLE, padding: '16px 8px 8px 0' }}>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={weightData30} margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.subtle, fontFamily: FONT_BODY }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis hide domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.fill}`, fontFamily: FONT_BODY, fontSize: 12 }}
                    formatter={v => [`${v} kg`, 'Peso']}
                    labelStyle={{ color: C.text, fontWeight: 600 }}
                    cursor={{ stroke: C.subtle, strokeWidth: 1 }}
                  />
                  <Line type="monotone" dataKey="peso" stroke={C.green} strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: C.green }} />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 16px 4px', fontSize: 12, color: C.subtle, fontFamily: FONT_BODY }}>
                <span>{weightData30[0]?.peso} kg (inizio)</span>
                <span style={{ fontWeight: 600, color: C.text }}>{weightData30[weightData30.length - 1]?.peso} kg (ora)</span>
              </div>
            </div>
          </>
        )}

        {/* ── Metriche corporee ── */}
        {bodyMetrics && (
          <>
            <SectionTitle>METRICHE CORPOREE</SectionTitle>
            <div style={{ ...St.card }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, textAlign: 'center', padding: '14px 8px', background: `${bodyMetrics.bmiCol}12`, borderRadius: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>BMI</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: bodyMetrics.bmiCol, letterSpacing: -1, lineHeight: 1 }}>{bodyMetrics.bmi.toFixed(1)}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: bodyMetrics.bmiCol, marginTop: 5 }}>{bodyMetrics.bmiCat}</div>
                </div>
                {bodyMetrics.bmr && (
                  <div style={{ flex: 1, textAlign: 'center', padding: '14px 8px', background: `${C.blue}10`, borderRadius: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>BMR</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: -0.5, lineHeight: 1 }}>{bodyMetrics.bmr.toLocaleString('it-IT')}</div>
                    <div style={{ fontSize: 10, color: C.subtle, marginTop: 5 }}>kcal/die</div>
                  </div>
                )}
                {bodyMetrics.tdee && (
                  <div style={{ flex: 1, textAlign: 'center', padding: '14px 8px', background: `${C.green}10`, borderRadius: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>TDEE</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: -0.5, lineHeight: 1 }}>{bodyMetrics.tdee.toLocaleString('it-IT')}</div>
                    <div style={{ fontSize: 10, color: C.subtle, marginTop: 5 }}>kcal/die</div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── Stato esercizi ── */}
        {totalEx > 0 && (
          <>
            <SectionTitle>STATO ESERCIZI (28 GG)</SectionTitle>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              {STATE_ORDER.map(s => {
                const count = grouped[s]?.length || 0;
                if (!count) return null;
                const col = STATE_COLORS[s];
                return (
                  <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, background: col.bg, border: `1px solid ${col.border}`, fontSize: 12, fontWeight: 600, color: col.color, fontFamily: FONT_BODY }}>
                    <span style={{ fontSize: 14, fontWeight: 800 }}>{count}</span>
                    <span>{s}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ ...CARD_STYLE, padding: '4px 0', overflow: 'hidden' }}>
              {statesToShow.map((state, si) => {
                const items = grouped[state];
                if (!items?.length) return null;
                const visible = progExpanded ? items : items.slice(0, PROGRESS_LIMIT);
                return (
                  <div key={state}>
                    {si > 0 && <div style={{ height: '0.5px', background: 'rgba(60,60,67,0.10)', margin: '0 16px' }} />}
                    <div style={{ padding: '10px 16px 4px', fontSize: 11, fontWeight: 700, color: STATE_COLORS[state]?.color || C.subtle, fontFamily: FONT_BODY, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      {state}
                    </div>
                    {visible.map((item, i) => (
                      <div key={item.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 16px', borderBottom: i < visible.length - 1 ? '0.5px solid rgba(60,60,67,0.08)' : 'none' }}>
                        <span style={{ fontSize: 14, color: C.text, fontFamily: FONT_BODY, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 10 }}>
                          {item.name}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          {item.pctChange !== undefined && Math.abs(item.pctChange) >= 1 && (
                            <span style={{ fontSize: 12, fontWeight: 600, color: item.pctChange > 0 ? C.green : C.red, fontFamily: FONT_BODY }}>
                              {item.pctChange > 0 ? '+' : ''}{item.pctChange.toFixed(1)}%
                            </span>
                          )}
                          <StatePill state={item.state} />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
              {!progExpanded && totalEx > PROGRESS_LIMIT && (
                <button onClick={() => setProgExpanded(true)}
                  style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: C.blue, fontFamily: FONT_BODY, textAlign: 'center' }}>
                  Vedi tutti ({totalEx})
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Top 1RM ── */}
        {top1RM.length > 0 && (
          <>
            <SectionTitle>TOP 1RM STIMATO</SectionTitle>
            <div style={{ ...CARD_STYLE, padding: '4px 0', overflow: 'hidden' }}>
              {top1RM.map((item, i) => (
                <div key={item.name} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: i < top1RM.length - 1 ? '0.5px solid rgba(60,60,67,0.08)' : 'none' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 14, background: `${C.blue}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.blue, fontFamily: FONT_BODY }}>{i + 1}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: FONT_BODY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: FONT_BODY }}>{item.rm} kg</div>
                    {item.delta !== null && (
                      <div style={{ fontSize: 11, fontWeight: 600, color: item.delta >= 0 ? C.green : C.red, fontFamily: FONT_BODY }}>
                        {item.delta >= 0 ? '+' : ''}{item.delta} kg vs 4 sett.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Empty state */}
        {sessions.length === 0 && (
          <div style={{ ...CARD_STYLE, padding: '32px 20px', textAlign: 'center', marginTop: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: C.text, fontFamily: FONT_BODY, marginBottom: 8 }}>Nessun dato disponibile</div>
            <div style={{ fontSize: 14, color: C.subtle, fontFamily: FONT_BODY, lineHeight: 1.5 }}>
              Registra le tue sessioni di allenamento per vedere le statistiche.
            </div>
          </div>
        )}

        {/* ── Review Mensile ── */}
        {monthsWithSessions.length > 0 && (() => {
          const yearGroups = {};
          monthsWithSessions.forEach(m => {
            const y = m.key.slice(0, 4);
            if (!yearGroups[y]) yearGroups[y] = [];
            yearGroups[y].push(m);
          });
          return Object.keys(yearGroups).sort((a, b) => b - a).map(y => (
            <div key={y}>
              <SectionTitle mt={24}>{y}</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {yearGroups[y].map(m => {
                  const moIdx = parseInt(m.key.slice(5, 7), 10) - 1;
                  const moName = MONTH_NAMES_IT[moIdx];
                  const IllComp = MONTH_ILLUSTRATIONS[moIdx % MONTH_ILLUSTRATIONS.length];
                  const accentCol = MONTH_COLORS[moIdx % MONTH_COLORS.length];
                  return (
                    <button key={m.key} onClick={() => setSelectedMonthReview(m)}
                      className="gp-btn-press"
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'stretch',
                        background: '#fff', borderRadius: 16, border: 'none', cursor: 'pointer',
                        padding: 0, overflow: 'hidden', fontFamily: FONT_BODY, textAlign: 'left',
                        WebkitTapHighlightColor: 'transparent',
                        boxShadow: '0 1px 8px rgba(0,0,0,0.06)', aspectRatio: '1',
                      }}>
                      <div style={{ flex: 1, background: `${accentCol}0D`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px 18px 10px' }}>
                        <IllComp color={accentCol}/>
                      </div>
                      <div style={{ padding: '10px 14px 14px' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, letterSpacing: -0.2 }}>Review {moName}</div>
                        <div style={{ fontSize: 11, color: C.subtle, marginTop: 2 }}>{m.sessions.length} sessioni</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ));
        })()}

      </div>
    </div>

    {selectedMonthReview && (
      <MonthReviewOverlay
        monthData={selectedMonthReview}
        allSessions={sessions}
        onClose={() => setSelectedMonthReview(null)}
      />
    )}
</>
  );
}
